import { useMemo, useRef } from 'react';
import { PanResponder, StyleSheet, View } from 'react-native';
import { Gesture, GestureDetector, type GestureType } from 'react-native-gesture-handler';
import Svg, { Circle, Ellipse, Line, Path, Text as SvgText } from 'react-native-svg';
import { tokens } from '../../theme/tokens';
import type {
  ConstraintDragState,
  DiagramSemanticProfile,
  DiagramType,
} from '../../types/diagramSpec';
import {
  projectPointToConstraint,
  projectTrianglePoint,
  resolveConstraintDragRelease,
} from '../../utils/diagramSpecMock';

type RedrawnDiagramCanvasProps = {
  diagramType: DiagramType;
  semanticProfile: DiagramSemanticProfile;
  parameter: number;
  dragState: ConstraintDragState;
  interactive: boolean;
  zoom: number;
  panOffset: { x: number; y: number };
  showAuxiliaryLayer: boolean;
  onParameterChange: (parameter: number, state: ConstraintDragState) => void;
  onDragStateChange: (state: ConstraintDragState) => void;
  onPanOffsetChange: (offset: { x: number; y: number }) => void;
};

type ConstraintDragHandleProps = {
  x: number;
  y: number;
  state: ConstraintDragState;
  interactive: boolean;
};

type NativePanHandlers = ReturnType<typeof PanResponder.create>['panHandlers'];

const handleStateMeta: Record<ConstraintDragState, { fill: string; stroke: string; strokeWidth: number; glyph: string; dash?: string }> = {
  idle: { fill: tokens.colors.starCyan, stroke: tokens.colors.starText, strokeWidth: 2, glyph: '' },
  pressed: { fill: tokens.colors.starCyan, stroke: tokens.colors.starAmber, strokeWidth: 4, glyph: '' },
  dragging: { fill: tokens.colors.starCyan, stroke: tokens.colors.white, strokeWidth: 3, glyph: '↔', dash: '3 2' },
  boundary: { fill: tokens.colors.starAmber, stroke: tokens.colors.starText, strokeWidth: 2, glyph: '!' },
  computing: { fill: tokens.colors.starViolet, stroke: tokens.colors.starText, strokeWidth: 2, glyph: '…' },
  invalid: { fill: tokens.colors.starDanger, stroke: tokens.colors.starText, strokeWidth: 2, glyph: '×' },
  disabled: { fill: tokens.colors.starMuted, stroke: tokens.colors.starText, strokeWidth: 2, glyph: '−' },
  reset: { fill: tokens.colors.success, stroke: tokens.colors.starText, strokeWidth: 2, glyph: '↺' },
};

export function ConstraintDragHandle({ x, y, state, interactive }: ConstraintDragHandleProps) {
  const meta = handleStateMeta[state];

  return (
    <>
      <Circle accessibilityLabel={`动点 P 状态：${state}`} cx={x} cy={y} r={22} fill="transparent" />
      <Circle cx={x} cy={y} r={12} fill={meta.fill} stroke={meta.stroke} strokeWidth={meta.strokeWidth} strokeDasharray={meta.dash} />
      {meta.glyph ? <SvgText x={x - 4} y={y + 5} fill={tokens.colors.deepSpace} fontSize={13} fontWeight="700">{meta.glyph}</SvgText> : null}
      <SvgText x={x + 18} y={y + 5} fill={tokens.colors.starText} fontSize={15} fontWeight="700">P</SvgText>
      {!interactive ? <SvgText x={x - 9} y={y + 38} fill={tokens.colors.starMuted} fontSize={10}>{state === 'invalid' ? '约束失效' : '静态'}</SvgText> : null}
    </>
  );
}

function ConstraintDragOverlay({
  point,
  interactive,
  gesture,
}: {
  point: { x: number; y: number };
  interactive: boolean;
  gesture: GestureType;
}) {
  return (
    <GestureDetector gesture={gesture}>
      <View
        accessibilityLabel="动点 P，可沿已确认轨迹拖动"
        accessibilityRole="adjustable"
        collapsable={false}
        pointerEvents={interactive ? 'auto' : 'none'}
        style={[
          styles.pointDragOverlay,
          { left: `${(point.x / 340) * 100}%`, top: point.y },
        ]}
      />
    </GestureDetector>
  );
}

function CanvasPanOverlay({ panHandlers }: { panHandlers: NativePanHandlers }) {
  return <View pointerEvents="auto" style={styles.canvasPanOverlay} {...panHandlers} />;
}

export function RedrawnDiagramCanvas({
  diagramType,
  semanticProfile,
  parameter,
  dragState,
  interactive,
  zoom,
  panOffset,
  showAuxiliaryLayer,
  onParameterChange,
  onDragStateChange,
  onPanOffsetChange,
}: RedrawnDiagramCanvasProps) {
  const point = diagramType === 'triangle' ? projectTrianglePoint(parameter) : projectPointToConstraint(parameter).point;
  const dragStartParameterRef = useRef(parameter);
  const panStartOffsetRef = useRef(panOffset);
  const parameterRef = useRef(parameter);
  const onParameterChangeRef = useRef(onParameterChange);
  const onDragStateChangeRef = useRef(onDragStateChange);
  const constraintDragDistance = 160;

  parameterRef.current = parameter;
  onParameterChangeRef.current = onParameterChange;
  onDragStateChangeRef.current = onDragStateChange;
  const panResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => false,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderGrant: () => {
      panStartOffsetRef.current = panOffset;
    },
    onPanResponderMove: (_event, gesture) => {
      onPanOffsetChange({ x: panStartOffsetRef.current.x + gesture.dx, y: panStartOffsetRef.current.y + gesture.dy });
    },
    onPanResponderRelease: (_event, gesture) => {
      onPanOffsetChange({ x: panStartOffsetRef.current.x + gesture.dx, y: panStartOffsetRef.current.y + gesture.dy });
    },
    onPanResponderTerminate: (_event, gesture) => {
      onPanOffsetChange({ x: panStartOffsetRef.current.x + gesture.dx, y: panStartOffsetRef.current.y + gesture.dy });
    },
  });
  const pointDragGesture = useMemo(() => Gesture.Pan()
    .enabled(interactive)
    .minDistance(0)
    .onBegin(() => {
      dragStartParameterRef.current = parameterRef.current;
      onDragStateChangeRef.current('pressed');
    })
    .onStart(() => {
      onDragStateChangeRef.current('dragging');
    })
    .onUpdate((event) => {
      const result = projectPointToConstraint(dragStartParameterRef.current + event.translationX / constraintDragDistance);
      onParameterChangeRef.current(result.parameter, result.dragState);
    })
    .onFinalize((event) => {
      const result = resolveConstraintDragRelease(dragStartParameterRef.current, event.translationX, constraintDragDistance);
      onParameterChangeRef.current(result.parameter, result.dragState);
    })
    .runOnJS(true), [interactive]);

  return (
    <View
      accessibilityLabel={interactive ? '数学重绘画布，点 P 可沿已确认轨迹拖动' : '数学重绘画布，当前为静态模式'}
      accessibilityRole="image"
      style={styles.canvas}
    >
      <View style={[styles.svgLayer, { transform: [{ translateX: panOffset.x }, { translateY: panOffset.y }, { scale: zoom }] }]}>
      <Svg width="100%" height={268} viewBox="0 0 340 268">
        <SvgText x="18" y="28" fill={tokens.colors.starText} fontSize={18} fontWeight="700">
          {diagramType === 'function' ? '二次函数 · 清晰重绘' : diagramType === 'conic' ? '标准椭圆' : diagramType === 'triangle' ? '三角形动点' : '圆与辅助线'}
        </SvgText>
        <SvgText x="18" y="48" fill={tokens.colors.starMuted} fontSize={12}>AI 重绘，供理解参考</SvgText>
        {diagramType === 'function' ? <FunctionDiagram point={point} showAuxiliaryLayer={showAuxiliaryLayer} /> : null}
        {diagramType === 'conic' ? <ConicDiagram showAuxiliaryLayer={showAuxiliaryLayer} /> : null}
        {diagramType === 'triangle' ? <TriangleDiagram point={point} showAuxiliaryLayer={showAuxiliaryLayer} /> : null}
        {diagramType === 'circle' ? (
          <CircleDiagram
            semanticProfile={semanticProfile}
            showAuxiliaryLayer={showAuxiliaryLayer}
          />
        ) : null}
        {(diagramType === 'function' || diagramType === 'triangle') ? (
          <ConstraintDragHandle x={point.x} y={point.y} state={dragState} interactive={interactive} />
        ) : null}
      </Svg>
      <CanvasPanOverlay panHandlers={panResponder.panHandlers} />
      {(diagramType === 'function' || diagramType === 'triangle') ? (
        <ConstraintDragOverlay point={point} interactive={interactive} gesture={pointDragGesture} />
      ) : null}
      </View>
    </View>
  );
}

function FunctionDiagram({ point, showAuxiliaryLayer }: { point: { x: number; y: number }; showAuxiliaryLayer: boolean }) {
  return (
    <>
      {showAuxiliaryLayer ? <Line x1="48" y1="186" x2="292" y2="186" stroke={tokens.colors.starLine} strokeWidth={1.5} /> : null}
      {showAuxiliaryLayer ? <Line x1="170" y1="64" x2="170" y2="222" stroke={tokens.colors.starAmber} strokeWidth={1.5} /> : null}
      {showAuxiliaryLayer ? <Line x1="92" y1="64" x2="92" y2="222" stroke={tokens.colors.starLine} strokeWidth={1.5} /> : null}
      <Path d="M48 66 C88 174, 122 184, 170 190 C218 184, 254 174, 292 66" fill="none" stroke={tokens.colors.starCyan} strokeWidth={3} />
      <SvgText x="18" y="246" fill={tokens.colors.starText} fontSize={14}>x = {Math.round((point.x - 170) / 24)} · y = {Math.round((186 - point.y) / 15)}</SvgText>
    </>
  );
}

function ConicDiagram({ showAuxiliaryLayer }: { showAuxiliaryLayer: boolean }) {
  return (
    <>
      <Ellipse cx="170" cy="148" rx="92" ry="56" fill="none" stroke={tokens.colors.starCyan} strokeWidth={2.5} />
      {showAuxiliaryLayer ? <Line x1="78" y1="148" x2="262" y2="148" stroke={tokens.colors.starLine} strokeWidth={1.5} /> : null}
      {showAuxiliaryLayer ? <Circle cx="126" cy="148" r="5" fill={tokens.colors.starAmber} /> : null}
      {showAuxiliaryLayer ? <Circle cx="214" cy="148" r="5" fill={tokens.colors.starAmber} /> : null}
      <SvgText x="18" y="246" fill={tokens.colors.starText} fontSize={14}>焦点 F1 / F2 · 长轴已确认</SvgText>
    </>
  );
}

function TriangleDiagram({ point, showAuxiliaryLayer }: { point: { x: number; y: number }; showAuxiliaryLayer: boolean }) {
  return (
    <>
      <Path d="M78 196 L170 72 L262 196 Z" fill="none" stroke={tokens.colors.starCyan} strokeWidth={2.5} />
      {showAuxiliaryLayer ? <Line x1="170" y1="72" x2={point.x} y2={point.y} stroke={tokens.colors.starAmber} strokeWidth={2.5} /> : null}
      <SvgText x="163" y="63" fill={tokens.colors.starText} fontSize={14}>A</SvgText>
      <SvgText x="68" y="205" fill={tokens.colors.starText} fontSize={14}>B</SvgText>
      <SvgText x="266" y="205" fill={tokens.colors.starText} fontSize={14}>C</SvgText>
      <SvgText x="18" y="246" fill={tokens.colors.starText} fontSize={14}>D 在线段 BC 上，AD 随动</SvgText>
    </>
  );
}

function CircleDiagram({
  semanticProfile,
  showAuxiliaryLayer,
}: {
  semanticProfile: DiagramSemanticProfile;
  showAuxiliaryLayer: boolean;
}) {
  if (semanticProfile === 'circle_diameter_extended_tangents') {
    return (
      <>
        <Circle cx="170" cy="148" r="58" fill="none" stroke={tokens.colors.starCyan} strokeWidth={2.5} />
        <Line x1="72" y1="148" x2="268" y2="148" stroke={tokens.colors.starText} strokeWidth={1.8} />
        {showAuxiliaryLayer ? <Line x1="112" y1="72" x2="112" y2="224" stroke={tokens.colors.starAmber} strokeWidth={2} /> : null}
        {showAuxiliaryLayer ? <Line x1="228" y1="72" x2="228" y2="224" stroke={tokens.colors.starAmber} strokeWidth={2} /> : null}
        <Circle cx="170" cy="148" r="5" fill={tokens.colors.starAmber} />
        <SvgText x="18" y="246" fill={tokens.colors.starText} fontSize={14}>圆 · 直径 · 延长线 · 两条切线</SvgText>
      </>
    );
  }

  return (
    <>
      <Circle cx="170" cy="148" r="68" fill="none" stroke={tokens.colors.starCyan} strokeWidth={2.5} />
      {showAuxiliaryLayer ? <Line x1="112" y1="104" x2="232" y2="188" stroke={tokens.colors.starText} strokeWidth={1.8} /> : null}
      {showAuxiliaryLayer ? <Circle cx="170" cy="148" r="5" fill={tokens.colors.starAmber} /> : null}
      <SvgText x="18" y="246" fill={tokens.colors.starText} fontSize={14}>圆 O · 弦 AB · M 为中点 · OM 垂直 AB</SvgText>
    </>
  );
}

const styles = StyleSheet.create({
  canvas: {
    minHeight: 268,
    overflow: 'hidden',
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: tokens.colors.starCyan,
    backgroundColor: tokens.colors.deepSpace,
  },
  svgLayer: {
    width: '100%',
    height: 268,
    position: 'relative',
  },
  pointDragOverlay: {
    position: 'absolute',
    width: 44,
    height: 44,
    marginLeft: -22,
    marginTop: -22,
    zIndex: 2,
  },
  canvasPanOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 268,
    zIndex: 1,
  },
});
